import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Calendar } from 'lucide-react';
import { EthicalHero } from '../ui/hero-5';
import { cabinetInfo, team } from '../../mock';

export default function HeroV2({ openNow, scrollToSection }) {
  const features = [
    {
      id: 'soins',
      title: 'Soins dentaires',
      subtitle: 'Consultations, détartrage, soins préventifs et bilan complet pour toute la famille.',
      imageUrl: '/image/service-soins.webp',
      onClick: () => scrollToSection('services'),
    },
    {
      id: 'implants',
      title: 'Implants & prothèses',
      subtitle: 'Solutions permanentes pour remplacer une ou plusieurs dents — résultats naturels et durables.',
      imageUrl: '/image/service-implants.webp',
      onClick: () => scrollToSection('specialites'),
    },
    {
      id: 'orthodontie',
      title: 'Orthodontie & gouttières',
      subtitle: 'Bagues fixes ou gouttières transparentes fabriquées au cabinet, pour enfants et adultes.',
      imageUrl: '/image/service-aligners.webp',
      onClick: () => scrollToSection('specialites'),
    },
  ];

  const titleNode = (
    <>
      Votre sourire,
      <br />
      notre <span className="italic text-amber-600">engagement</span>.
    </>
  );

  return (
    <section
      id="accueil"
      className="relative bg-background overflow-hidden pt-24 lg:pt-28"
    >
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[60rem] h-[40rem] rounded-full bg-amber-200/25 blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.6 }}
        className="relative z-10 max-w-6xl mx-auto px-4 pt-6 flex justify-center"
      >
        <span className="inline-flex items-center gap-2 rounded-full bg-foreground/5 text-foreground/70 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.25em] ring-1 ring-foreground/10 backdrop-blur">
          <MapPin size={12} aria-hidden="true" />
          Courcelles-lès-Lens · Pas-de-Calais
        </span>
      </motion.div>

      <EthicalHero
        title={titleNode}
        subtitle="Cabinet dentaire pluridisciplinaire à 5 minutes de Lens et Hénin-Beaumont. Une équipe de chirurgiens-dentistes expérimentés et un plateau technique moderne — pour un sourire qui vous ressemble."
        ctaLabel="Prendre rendez-vous"
        ctaHref={cabinetInfo.doctolib}
        ctaTarget="_blank"
        ctaRel="noopener noreferrer"
        features={features}
        className="!pt-8 !pb-12 sm:!pt-12 sm:!pb-16"
        titleClassName="font-display tracking-tight text-foreground text-5xl sm:text-7xl lg:text-8xl leading-[0.9]"
        subtitleClassName="text-base lg:text-lg text-foreground/65 leading-relaxed max-w-2xl mx-auto"
        ctaClassName="rounded-full bg-foreground text-background hover:bg-amber-500 hover:text-foreground hover:-translate-y-0.5 transition-all uppercase tracking-[0.2em] text-xs h-12 px-7"
        cardClassName="ring-1 ring-foreground/10 hover:ring-foreground/30"
        cardTitleClassName="font-display text-2xl tracking-tight"
      />

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.4, duration: 0.6 }}
        className="relative z-10 max-w-6xl mx-auto px-4 pb-16 lg:pb-20"
      >
        <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-[10px] uppercase tracking-[0.25em] text-foreground/55">
          <span className="inline-flex items-center gap-2">
            <span className="relative flex w-2 h-2">
              {openNow && (
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-60 animate-ping motion-reduce:hidden" />
              )}
              <span className={`relative inline-flex w-2 h-2 rounded-full ${openNow ? 'bg-emerald-500' : 'bg-foreground/40'}`} />
            </span>
            <span className={`font-semibold ${openNow ? 'text-emerald-700' : 'text-foreground/70'}`}>
              {openNow ? 'Ouvert maintenant' : 'Fermé actuellement'}
            </span>
          </span>

          <span className="inline-flex items-center gap-3">
            <span aria-hidden="true" className="flex -space-x-1.5">
              {team.slice(0, 3).map((m) => (
                <span
                  key={m.id}
                  className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-foreground text-background text-[10px] font-bold ring-2 ring-background"
                >
                  {m.initials}
                </span>
              ))}
            </span>
            <span className="text-foreground/70">3 chirurgiens-dentistes titulaires</span>
          </span>

          <a
            href={`tel:${cabinetInfo.phone[0].replace(/\s/g, '')}`}
            className="inline-flex items-center gap-2 hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground rounded"
            aria-label={`Appeler le cabinet au ${cabinetInfo.phone[0]}`}
          >
            <Calendar size={12} aria-hidden="true" />
            {cabinetInfo.phone[0]}
          </a>

          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(cabinetInfo.address)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground rounded"
          >
            75 Av. Régis Pollet
          </a>
        </div>
      </motion.div>
    </section>
  );
}
